import { Loader2 } from "lucide-react";

import type { IPro014EditSnapshot } from "@/@types/campaign";
import { CampaignType } from "@/constants/campaign-types";
import { useGetPro014EditSnapshotQuery } from "@/services/campaign-pro014";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";

import { buildEmptyPro014Data, buildPro014EditData } from "../../utils/pro014-update-diff";
import { PRO014_WIZARD_MODE, PRO014_WIZARD_TEXTS } from "../constants";
import type { Pro014EditSource, Pro014WizardMode } from "../types";
import { Pro014Wizard } from "./pro014-wizard";

interface CreatePro014FormProps {
  onChangeType?: (type: CampaignType) => void;
  mode?: Pro014WizardMode;
  campaignCode?: number;
}

interface Pro014EditLoaderProps {
  campaignCode: number;
}

function Pro014EditLoader({ campaignCode }: Pro014EditLoaderProps): JSX.Element {
  const { data, isError, error } = useGetPro014EditSnapshotQuery(campaignCode, {
    refetchOnMountOrArgChange: true,
  });
  const snapshot: IPro014EditSnapshot | undefined = data;

  if (snapshot) {
    const editSource: Pro014EditSource = {
      campaignCode,
      detail: snapshot.detail,
      items: snapshot.items,
    };
    return (
      <Pro014Wizard
        mode={PRO014_WIZARD_MODE.EDIT}
        initialData={buildPro014EditData(editSource)}
        editSource={editSource}
      />
    );
  }

  if (isError) {
    return (
      <div className="mx-auto flex h-64 w-full max-w-4xl items-center justify-center">
        <p className="text-sm text-destructive">
          {extractRtkErrorMessage(error, PRO014_WIZARD_TEXTS.LOAD_FAILED)}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto flex h-64 w-full max-w-4xl items-center justify-center">
      <div className="text-center">
        <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-primary" aria-hidden />
        <p className="text-muted-foreground">{PRO014_WIZARD_TEXTS.LOADING}</p>
      </div>
    </div>
  );
}

export default function CreatePro014Form({
  onChangeType,
  mode = PRO014_WIZARD_MODE.CREATE,
  campaignCode,
}: CreatePro014FormProps): JSX.Element {
  if (mode === PRO014_WIZARD_MODE.EDIT && campaignCode) {
    return <Pro014EditLoader campaignCode={campaignCode} />;
  }

  return (
    <Pro014Wizard
      mode={PRO014_WIZARD_MODE.CREATE}
      initialData={buildEmptyPro014Data()}
      onChangeType={onChangeType}
    />
  );
}
